import { resolveAdvisorV2HttpBaseUrl } from './resolve-advisor-v2-http-base-url';

/** Relative path on the agent chat HTTP surface (same host as stream / threads). */
export const AGENT_UI_TEMPLATES_PATH = '/v2/chat/ui-templates';

export type AgentUiTemplatePlaceholderSpec = { default: string };

export type AgentUiTemplateTextItem = { id: string; text: string; route_use_case?: string };

export type AgentUiTemplateTemplateItem = {
  id: string;
  text_template: string;
  placeholders: Record<string, AgentUiTemplatePlaceholderSpec>;
};

export type AgentUiTemplateItem = AgentUiTemplateTextItem | AgentUiTemplateTemplateItem;

export type AgentUiTemplateSection = {
  id: string;
  title: string | null;
  items: AgentUiTemplateItem[];
};

export type AgentUiTemplatesPayload = {
  version: number;
  /** Echoed when the client sent ``subscription_tier`` (future plan-specific templates). */
  subscription_tier?: string;
  use_cases: Record<
    string,
    {
      scopes: Record<
        string,
        {
          sections: AgentUiTemplateSection[];
        }
      >;
    }
  >;
};

export type FetchAgentUiTemplatesInput = {
  /** Registered lane (``monitoring``, ``partner``, …) — must match stream ``use_case``. */
  useCase: string;
  /** Same Bearer + ``x-organization-id`` headers as ``POST /v2/chat/stream``. */
  headers?: HeadersInit;
    subscriptionTier?: string | null;
};

function assertUiTemplateItem(item: unknown, path: string): void {
  if (item === null || typeof item !== 'object' || Array.isArray(item)) {
    throw new Error(`Invalid UI templates response: ${path} item must be an object`);
  }
  const rec = item as Record<string, unknown>;
  if (typeof rec.id !== 'string' || rec.id.trim() === '') {
    throw new Error(`Invalid UI templates response: ${path} item.id must be a non-empty string`);
  }
  if ('text_template' in rec) {
    if (typeof rec.text_template !== 'string') {
      throw new Error(`Invalid UI templates response: ${path} template item must have string text_template`);
    }
    const ph = rec.placeholders;
    if (ph !== undefined && (ph === null || typeof ph !== 'object' || Array.isArray(ph))) {
      throw new Error(`Invalid UI templates response: ${path} template item placeholders must be an object`);
    }
    if (ph && typeof ph === 'object' && !Array.isArray(ph)) {
      for (const [k, spec] of Object.entries(ph as Record<string, unknown>)) {
        if (spec === null || typeof spec !== 'object' || Array.isArray(spec)) {
          throw new Error(`Invalid UI templates response: ${path} placeholder "${k}" must be an object`);
        }
        const d = (spec as Record<string, unknown>).default;
        if (typeof d !== 'string') {
          throw new Error(`Invalid UI templates response: ${path} placeholder "${k}" must have string default`);
        }
      }
    }
    return;
  }
  if (typeof rec.text !== 'string') {
    throw new Error(`Invalid UI templates response: ${path} text item must have string text`);
  }
  const ru = rec.route_use_case;
  if (ru !== undefined && ru !== null) {
    if (typeof ru !== 'string' || ru.trim() === '') {
      throw new Error(
        `Invalid UI templates response: ${path} route_use_case must be a non-empty string when present`,
      );
    }
  }
}

function assertUiTemplateSection(section: unknown, path: string): void {
  if (section === null || typeof section !== 'object' || Array.isArray(section)) {
    throw new Error(`Invalid UI templates response: ${path} section must be an object`);
  }
  const s = section as Record<string, unknown>;
  if (typeof s.id !== 'string' || s.id.trim() === '') {
    throw new Error(`Invalid UI templates response: ${path} section.id must be a non-empty string`);
  }
  if (s.title !== null && s.title !== undefined && typeof s.title !== 'string') {
    throw new Error(`Invalid UI templates response: ${path} section.title must be string or null`);
  }
  if (!Array.isArray(s.items)) {
    throw new Error(`Invalid UI templates response: ${path} section.items must be an array`);
  }
  s.items.forEach((it, i) => assertUiTemplateItem(it, `${path}.items[${i}]`));
}

export function parseAgentUiTemplatesPayload(raw: unknown): AgentUiTemplatesPayload {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Invalid UI templates response: expected a JSON object');
  }
  const o = raw as Record<string, unknown>;
  if (typeof o.version !== 'number' || !Number.isFinite(o.version)) {
    throw new Error('Invalid UI templates response: missing numeric version');
  }
  if (o.subscription_tier !== undefined && o.subscription_tier !== null && typeof o.subscription_tier !== 'string') {
    throw new Error('Invalid UI templates response: subscription_tier must be a string when present');
  }
  if (o.use_cases === null || typeof o.use_cases !== 'object' || Array.isArray(o.use_cases)) {
    throw new Error('Invalid UI templates response: missing use_cases object');
  }
  const useCases = o.use_cases as Record<string, unknown>;
  for (const [useCaseKey, useCaseVal] of Object.entries(useCases)) {
    if (useCaseVal === null || typeof useCaseVal !== 'object' || Array.isArray(useCaseVal)) {
      throw new Error(`Invalid UI templates response: use_cases.${useCaseKey} must be an object`);
    }
    const uc = useCaseVal as Record<string, unknown>;
    if (uc.scopes === null || typeof uc.scopes !== 'object' || Array.isArray(uc.scopes)) {
      throw new Error(`Invalid UI templates response: use_cases.${useCaseKey}.scopes must be an object`);
    }
    const scopes = uc.scopes as Record<string, unknown>;
    for (const [scopeKey, scopeVal] of Object.entries(scopes)) {
      if (scopeVal === null || typeof scopeVal !== 'object' || Array.isArray(scopeVal)) {
        throw new Error(
          `Invalid UI templates response: use_cases.${useCaseKey}.scopes.${scopeKey} must be an object`,
        );
      }
      const sc = scopeVal as Record<string, unknown>;
      if (!Array.isArray(sc.sections)) {
        throw new Error(
          `Invalid UI templates response: use_cases.${useCaseKey}.scopes.${scopeKey}.sections must be an array`,
        );
      }
      sc.sections.forEach((sec, i) =>
        assertUiTemplateSection(sec, `use_cases.${useCaseKey}.scopes.${scopeKey}.sections[${i}]`),
      );
    }
  }
  return raw as AgentUiTemplatesPayload;
}

export async function fetchAgentUiTemplates(
  baseUrl: string | undefined,
  input: FetchAgentUiTemplatesInput,
): Promise<AgentUiTemplatesPayload> {
  const origin = resolveAdvisorV2HttpBaseUrl(baseUrl);
  const search = new URLSearchParams({ use_case: input.useCase });
  const tier = input.subscriptionTier?.trim();
  if (tier) search.set('subscription_tier', tier);
  const url = `${origin.replace(/\/$/, '')}${AGENT_UI_TEMPLATES_PATH}?${search.toString()}`;
  const response = await globalThis.fetch(url, { headers: input.headers });
  if (!response.ok) {
    throw new Error(`GET ${AGENT_UI_TEMPLATES_PATH} failed: ${response.status}`);
  }
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new Error(`GET ${AGENT_UI_TEMPLATES_PATH} failed: response was not JSON`);
  }
  return parseAgentUiTemplatesPayload(raw);
}

function isTemplateItem(item: AgentUiTemplateItem): item is AgentUiTemplateTemplateItem {
  return 'text_template' in item;
}

function resolveItemToText(item: AgentUiTemplateItem, values: Record<string, string>): string {
  if (!isTemplateItem(item)) {
    return item.text;
  }
  let out = item.text_template;
  for (const [key, spec] of Object.entries(item.placeholders ?? {})) {
    const resolved = values[key] ?? spec.default;
    out = out.split(`{${key}}`).join(resolved);
  }
  return out;
}

/** Flatten all items in order across sections and apply placeholder values. */
export function resolveAgentUiTemplateSuggestionTexts(
  sections: AgentUiTemplateSection[] | undefined,
  placeholderValues: Record<string, string>,
): string[] {
  if (!sections?.length) return [];
  const texts: string[] = [];
  for (const section of sections) {
    for (const item of section.items ?? []) {
      texts.push(resolveItemToText(item, placeholderValues));
    }
  }
  return texts;
}

/** One resolved chip from ``GET /v2/chat/ui-templates`` (monitoring org chips may include ``route_use_case``). */
export type AgentUiTemplateResolvedItem = { id: string; text: string; route_use_case?: string };

/** One row per API section so clients can show Figma section titles (not inferred from text). */
export type AgentUiTemplateResolvedRow = {
  title: string | null;
  items: AgentUiTemplateResolvedItem[];
};

export function resolveAgentUiTemplateRows(
  sections: AgentUiTemplateSection[] | undefined,
  placeholderValues: Record<string, string>,
): AgentUiTemplateResolvedRow[] {
  if (!sections?.length) return [];
  const out: AgentUiTemplateResolvedRow[] = [];
  for (const section of sections) {
    const items: AgentUiTemplateResolvedItem[] = [];
    for (const item of section.items ?? []) {
      if (isTemplateItem(item)) {
        items.push({ id: item.id, text: resolveItemToText(item, placeholderValues) });
      } else {
        const plain = item as AgentUiTemplateTextItem;
        const row: AgentUiTemplateResolvedItem = {
          id: plain.id,
          text: resolveItemToText(plain, placeholderValues),
        };
        const r = plain.route_use_case?.trim();
        if (r) row.route_use_case = r;
        items.push(row);
      }
    }
    out.push({ title: section.title, items });
  }
  return out;
}

export function getAgentUiTemplateSections(
  payload: AgentUiTemplatesPayload | undefined,
  useCase: string,
  scope: string,
): AgentUiTemplateSection[] | undefined {
  return payload?.use_cases?.[useCase]?.scopes?.[scope]?.sections;
}
